'use client';

import React, { useEffect, useRef } from 'react';

export default function HeroSection() {
  const line1Ref = useRef<HTMLSpanElement>(null);
  const line2Ref = useRef<HTMLSpanElement>(null);
  const line3Ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const timers = [
    setTimeout(() => line1Ref?.current?.classList?.add('revealed'), 300),
    setTimeout(() => line2Ref?.current?.classList?.add('revealed'), 500),
    setTimeout(() => line3Ref?.current?.classList?.add('revealed'), 700)];

    return () => timers?.forEach(clearTimeout);
  }, []);

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16 px-5 md:px-10"
      style={{ backgroundColor: 'var(--background)' }}>
      
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-2/3 h-2/3 opacity-30"
          style={{ background: 'radial-gradient(ellipse at top right, rgba(200,150,90,0.12) 0%, transparent 70%)' }} />
        
        <div
          className="absolute bottom-0 left-0 w-1/2 h-1/2 opacity-20"
          style={{ background: 'radial-gradient(ellipse at bottom left, rgba(200,150,90,0.08) 0%, transparent 70%)' }} />
        
      </div>
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7">
            {/* Badge */}
            <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-7" style={{ backgroundColor: 'var(--secondary)', borderColor: 'var(--border)' }}>
              <span className="gold-dot" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--muted-foreground)' }}>Buchhaltungsbüro Aktas</span>
            </div>

            {/* Heading */}
            <h1 className="font-display text-hero font-semibold mb-5" style={{ color: 'var(--foreground)' }}>
              <span className="line-reveal hero-line-1">
                <span ref={line1Ref} className="line-reveal-inner">Ihre Zahlen.</span>
              </span>
              <span className="line-reveal hero-line-2 mt-1">
                <span ref={line2Ref} className="line-reveal-inner">
                  Unsere{' '}
                  <span className="italic" style={{ color: 'var(--accent)' }}>Stärke.</span>
                </span>
              </span>
              <span className="line-reveal hero-line-3 mt-1">
                <span ref={line3Ref} className="line-reveal-inner">Ihr Erfolg.</span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="hero-sub text-base md:text-xl leading-relaxed max-w-xl font-light" style={{ color: 'var(--muted-foreground)' }}>
              Professionelle Buchhaltung, Lohnabrechnung und Steuerberatung für Selbstständige und Unternehmen — persönlich, zuverlässig, kompetent.
            </p>

            {/* CTAs */}
            <div className="hero-cta flex flex-col sm:flex-row gap-3 mt-8">
              <a
                href="#kontakt"
                onClick={(e) => {e?.preventDefault();document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });}}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 font-semibold text-sm rounded-full transition-all duration-300 hover:scale-[1.03] group min-h-[52px]"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary)'}>
                
                Beratung anfragen
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="#leistungen"
                onClick={(e) => {e?.preventDefault();document.querySelector('#leistungen')?.scrollIntoView({ behavior: 'smooth' });}}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border font-semibold text-sm rounded-full transition-all duration-300 min-h-[52px]"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onMouseEnter={(e) => {e.currentTarget.style.borderColor = 'var(--accent)';e.currentTarget.style.color = 'var(--accent)';}}
                onMouseLeave={(e) => {e.currentTarget.style.borderColor = 'var(--border)';e.currentTarget.style.color = 'var(--foreground)';}}>
                
                Leistungen entdecken
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="lg:col-span-5 relative h-[340px] sm:h-[420px] lg:h-[580px] mt-6 lg:mt-0 pb-12 sm:pb-14 lg:pb-0">
            {/* Main image card */}
            <div className="floating-card-1 absolute top-0 right-0 w-full max-w-sm lg:max-w-none lg:w-[90%] rounded-3xl overflow-hidden shadow-2xl shadow-black/15 border" style={{ borderColor: 'var(--border)' }}>
              <div className="relative aspect-[4/3]">
                <img
                  alt="Helles Büro mit rundem dunklen Tisch und zwei Stühlen am Fenster — einladende Beratungsatmosphäre"
                  src="/assets/images/WhatsApp_Image_2026-08-05_at_19.23.40__2_-1786029069665.jpeg"
                  className="object-cover w-full h-full"
                  style={{ position: 'absolute', inset: 0, objectFit: 'cover' }} />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                {/* Overlay card */}
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-3 border border-border/50">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--accent)' }}>
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Persönliche Beratung</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Individuelle Lösungen für Sie</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Software partner badges */}
            <div className="floating-card-2 absolute bottom-0 left-0 sm:-left-4 lg:left-0 z-10 flex flex-wrap gap-2 sm:gap-3">
              {/* Lexoffice */}
              <div className="rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 shadow-xl shadow-black/20 flex items-center gap-2 sm:gap-3" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(200,150,90,0.2)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--accent)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Lexoffice</p>
                  <p className="text-xs font-medium hidden sm:block" style={{ color: 'rgba(247,245,242,0.8)' }}>Digitale Buchhaltung</p>
                </div>
              </div>
              {/* DATEV */}
              <div className="rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 shadow-xl shadow-black/20 flex items-center gap-2 sm:gap-3" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(200,150,90,0.2)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--accent)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>DATEV</p>
                  <p className="text-xs font-medium hidden sm:block" style={{ color: 'rgba(247,245,242,0.8)' }}>Steuer & Lohn</p>
                </div>
              </div>
              {/* Addison */}
              <div className="rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 shadow-xl shadow-black/20 flex items-center gap-2 sm:gap-3" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(200,150,90,0.2)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--accent)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Addison</p>
                  <p className="text-xs font-medium hidden sm:block" style={{ color: 'rgba(247,245,242,0.8)' }}>Buchhaltungssoftware</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Scrollen</span>
        <div className="w-px h-10 bg-gradient-to-b" style={{ background: `linear-gradient(to bottom, var(--border), transparent)` }} />
      </div>
    </section>);

}